'use client'

import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { type Address, type Hex, parseAbiItem } from 'viem'
import { getLogs } from 'viem/actions'
import { useClient, useConfig } from 'wagmi'
import { getPublicClient } from 'wagmi/actions'

import type { ChainId } from '../configs/chains'

import { useUserAccount } from '../providers/UserAccountProvider'
import { maybeGetAddress } from '../utils/account.utils'
import { fetchSafeWalletHealth, type SafeWalletHealthWarning } from '../utils/safe-wallet.utils'
import { SAFE_CONNECTOR_ID } from '../utils/wallet-capabilities.utils'
import { useWalletConnectMetadata } from './useWalletConnectMetadata'

// Returns true when using a Safe Smart account:
// - app running as a Safe App
// - user connected via WalletConnect to a Safe Account
export function useIsSafeAccount(): boolean {
  const isSafeApp = useIsSafeApp()
  const { isSafeAccountViaWalletConnect } = useWalletConnectMetadata()
  return isSafeApp || isSafeAccountViaWalletConnect
}

// Returns true if the user is connected with a Safe Account
function useIsSafeApp(): boolean {
  const { connector } = useUserAccount()

  return connector?.id === SAFE_CONNECTOR_ID
}
export const DEFAULT_TX_TIMEOUT = 120_000
export const SAFE_WALLET_TX_TIMEOUT = 600_000
export const SAFE_WALLET_TX_TIMEOUT_MINUTES = Math.round(SAFE_WALLET_TX_TIMEOUT / 60_000)

export function useSafeTxHash({
  wagmiTxHash,
  chainId,
  blockNumber,
  enabled
}: {
  wagmiTxHash: Hex | undefined
  chainId: number
  blockNumber?: bigint
  enabled: boolean
}) {
  const { userAddress } = useUserAccount()
  const viemClient = useClient({ chainId })

  const queryFn = useCallback(
    () =>
      viemClient
        ? getLogs(viemClient, {
            event: parseAbiItem(['event ExecutionSuccess(bytes32 txHash, uint256 payment)']),
            address: userAddress,
            fromBlock: blockNumber
          })
        : [],
    [viemClient, userAddress, blockNumber]
  )

  // Get stable reference to `select`, otherwise Tanstack re-renders regardless
  const select = useCallback(
    (data: Awaited<ReturnType<typeof queryFn>>) =>
      data.find(log => log.args.txHash === wagmiTxHash),
    [wagmiTxHash]
  )

  const safeLogs = useQuery({
    queryKey: ['safeLogs', chainId, userAddress, wagmiTxHash],
    queryFn,
    select,
    refetchInterval: query =>
      query.state.data?.some(log => log.args.txHash === wagmiTxHash) ? 0 : 2000, // Refetch every 2 seconds until the safeTxHash is found
    // fromBlock follows the latest block, so a later refetch could miss the log it already found.
    staleTime: Infinity,
    enabled: enabled && !!wagmiTxHash && !!userAddress && !!viemClient
  })

  return { safeTxHash: safeLogs.data?.transactionHash }
}

type UseSafeWalletHealthParams = {
  address?: Address
  chainId: ChainId
  overlapAddress?: Address
}

type UseSafeWalletHealthResponse = {
  isSafeWallet: boolean | null
  isConfirmedEoa: boolean
  threshold?: number
  owners?: readonly Address[]
  healthWarnings: SafeWalletHealthWarning[]
  isLoading: boolean
  error: Error | null
}

/** @public */
export function useSafeWalletHealth({
  address,
  chainId,
  overlapAddress
}: UseSafeWalletHealthParams): UseSafeWalletHealthResponse {
  'use memo'

  const config = useConfig()
  const normalizedAddress = maybeGetAddress(address)
  const normalizedOverlapAddress = maybeGetAddress(overlapAddress)
  const query = useQuery({
    queryKey: ['safeWalletHealth', chainId, normalizedAddress, normalizedOverlapAddress],
    queryFn: () => {
      const client = getPublicClient(config, { chainId })
      if (!client || !normalizedAddress) throw new Error('Safe wallet health query is disabled')
      return fetchSafeWalletHealth(client, normalizedAddress, normalizedOverlapAddress)
    },
    enabled: !!address
  })

  if (query.isLoading || !address) {
    return {
      isSafeWallet: null,
      isConfirmedEoa: false,
      healthWarnings: [],
      isLoading: query.isLoading,
      error: null
    }
  }

  // RPC-level failure — wallet type is unknown; don't show a false EOA warning
  if (query.error || !query.data) {
    return {
      isSafeWallet: null,
      isConfirmedEoa: false,
      healthWarnings: [],
      isLoading: false,
      error: query.error
    }
  }

  // Contract-level reverts mean the address doesn't implement the Safe ABI → confirmed non-Safe
  if (!query.data.isSafeWallet) {
    return {
      isSafeWallet: false,
      isConfirmedEoa: true,
      healthWarnings: [],
      isLoading: false,
      error: null
    }
  }

  return {
    isSafeWallet: true,
    isConfirmedEoa: false,
    threshold: query.data.threshold,
    owners: query.data.owners,
    healthWarnings: query.data.healthWarnings,
    isLoading: false,
    error: null
  }
}
